import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Loading } from '../components/Loading'
import { StatusBadge } from '../components/StatusBadge'
import { downloadFeedbackPdf } from '../lib/pdf'
import { formatDate } from '../lib/utils'
import type { Submission } from '../types'

export function FeedbackPage() {
  const { submissionId } = useParams<{ submissionId: string }>()
  const [submission, setSubmission] = useState<Submission | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      if (!submissionId) {
        setError('Missing submission identifier.')
        setLoading(false)
        return
      }

      const { data: submissionData, error: submissionError } = await supabase
        .from('submissions')
        .select('*')
        .eq('id', submissionId)
        .single()

      if (submissionError || !submissionData) {
        setError('This private feedback could not be found.')
        setLoading(false)
        return
      }

      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedbacks')
        .select('*')
        .eq('submission_id', submissionId)
        .maybeSingle()

      if (feedbackError) {
        setError('The private feedback could not be loaded.')
      } else {
        setSubmission({
          ...(submissionData as Submission),
          feedbacks: feedbackData ? [feedbackData] : [],
        })
      }
      setLoading(false)
    }

    void load()
  }, [submissionId])

  if (loading) {
    return (
      <section className="section">
        <div className="container">
          <Loading label="Loading private feedback..." />
        </div>
      </section>
    )
  }

  if (error || !submission) {
    return (
      <section className="section">
        <div className="container narrow-container">
          <div className="notice notice-error">{error ?? 'Feedback unavailable.'}</div>
          <Link className="button button-secondary" to="/dashboard">
            Return to dashboard
          </Link>
        </div>
      </section>
    )
  }

  const feedback = submission.feedbacks?.[0]

  return (
    <section className="section">
      <div className="container narrow-container">
        <div className="feedback-header">
          <div>
            <p className="eyebrow">Private first look</p>
            <h1>{submission.public_code}</h1>
            <p>Published {formatDate(feedback?.published_at)}</p>
          </div>
          <StatusBadge status={submission.status} />
        </div>

        {!feedback ? (
          <div className="notice notice-neutral">
            No private feedback has been published for this submission.
          </div>
        ) : (
          <>
            <article className="feedback-document">
              <div className="feedback-label">
                {feedback.kind === 'notice' ? 'Private service notice' : 'Private feedback'}
              </div>
              <div className="feedback-body">
                {feedback.body.split('\n').map((line, index) => (
                  <p key={`${index}-${line.slice(0, 16)}`}>{line || '\u00a0'}</p>
                ))}
              </div>
            </article>

            <div className="card-actions feedback-actions">
              <button
                className="button button-primary"
                type="button"
                onClick={() => downloadFeedbackPdf(submission, feedback.body)}
              >
                Download feedback as PDF
              </button>
              <Link className="button button-secondary" to="/dashboard">
                Return to dashboard
              </Link>
            </div>

            <div className="notice notice-neutral">
              This is a one-way, informal first look. The platform does not provide a
              rebuttal, reply, clarification request, or follow-up discussion channel.
            </div>
          </>
        )}
      </div>
    </section>
  )
}
